import React from 'react';
import { PropTypes } from 'prop-types';
import { BrandingIcons, Button, ButtonGroup, DropdownBase, Heading, Icons, TYPES } from 'sarsaparilla';
import classNames from 'classnames';
import { get } from 'lodash';
import { domainToTileMap, generateTilePath, launchBuckets } from '../utilities/launchTiles';
import navigation from '../utilities/navigation';
import { displayStringForRoleType } from '../utilities/roles';
import LocationPicker from './LocationPicker';

const propTypes = {
  history: TYPES.HISTORY,
  user: PropTypes.shape({}),
  selectedRole: PropTypes.shape({}),
  selectedLocation: PropTypes.shape({}),
  logout: PropTypes.func.isRequired,
  navigateLogin: PropTypes.func,
  domainsForLocationId: PropTypes.shape({}),
  getDomainsForLocation: PropTypes.func,
  handleLocationSelectorClick: PropTypes.func,
};

export default class HeaderRibbon extends React.Component {
  constructor(props) {
    super(props);

    this.hoverTimer = null;
    this.state = {
      displayMenu: false,
    };

    this.toggleMenu = this.toggleMenu.bind(this);
  }

  onRibbonLeave() {
    this.hoverTimer = window.setTimeout(() => {
      this.hoverTimer = null;
      this.setState({
        displayMenu: false,
      });
    }, 350);
  }

  onRibbonEnter() {
    if (this.hoverTimer) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  toggleMenu() {
    this.setState({
      displayMenu: !this.state.displayMenu,
    });
  }

  userHoverUI = () => {
    const classes = classNames({
      'hover-user-view': true,
      invisible: !this.state.displayMenu,
    });

    return (
      <div className={classes}>
        <ul>
          <li>
            <Button
              appearance="link"
              id="profile-link"
              onClick={() => {
                navigation.push('/internal/account/my-profile', this.props.history);
              }}
            >
              My Profile
            </Button>
          </li>
          <li>
            <Button
              appearance="link"
              id="change-password-link"
              onClick={() => {
                navigation.push('/internal/account/change-password', this.props.history);
              }}
            >
              Change Password
            </Button>
          </li>
          <li>
            <Button
              appearance="link"
              id="logout-link"
              onClick={() => {
                this.props.logout();
              }}
            >
              Log Out
            </Button>
          </li>
        </ul>
      </div>
    );
  };

  userName() {
    if (this.props.user) {
      return `${this.props.user.first_name} ${this.props.user.last_name}`;
    }

    return '';
  }

  renderHeaderMain() {
    return (
      <header className="header-ribbon-main">
        <button
          className="header-hub-home-link"
          onClick={this.props.navigateLogin}
          role="link"
          aria-label="Recreation.gov | The Hub Homepage"
        >
          <span className="header-hub-home-link-inner">
            <span className="header-hub-logo">
              <BrandingIcons.LogoLight alt="Recreation.gov Hub" />
            </span>

            <span className="ribbon-logo-home-label">The Hub</span>
          </span>
        </button>

        <ButtonGroup className="header-ribbon-help-links">
          <Button
            appearance="link"
            iconBeforeElement={<Icons.IconLightBulb />}
            href="https://recgov-field.ideas.aha.io/"
            target="_blank"
            rel="noreferrer"
          >
            Ideas Portal
          </Button>
          <Button
            appearance="link"
            iconBeforeElement={<Icons.IconReport />}
            href="https://recreationonestopprod.servicenowservices.com/internalhub?id=kb_category&kb_category=71ad16461b27dc9051b5859ce54bcb80&kb_id=e104570adbf5db009bf57d0e0f9619f4"
            target="_blank"
            rel="noopener noreferrer"
          >
            System Updates
          </Button>

          <Button
            appearance="link"
            iconBeforeElement={<Icons.IconHelp />}
            href={process.env.SN_KB_INT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Help Center
          </Button>

          <Button
            onClick={() => window.open(process.env.SN_INTERNAL_CHAT_URL, 'Chat Support', 'width=300,height=600')}
            appearance="link"
            iconBeforeElement={<Icons.IconChat />}
            className="header-ribbon-contact-chat-btn"
          >
            Chat with us
          </Button>

          <Button
            appearance="link"
            onClick={() => this.toggleMenu()}
            iconBeforeElement={<Icons.IconAccountCircle />}
            size="lg"
          >
            {this.userName()}
          </Button>
        </ButtonGroup>
        {this.userHoverUI()}
      </header>
    );
  }

  renderLinks(bucketName, domains, colCount) {
    const links = [];

    let colPos = 0;
    domains.forEach((domain, i) => {
      const link = domainToTileMap[domain];
      if (link && link.bucket === bucketName) {
        colPos += 1;

        const col = Math.ceil(colPos / colCount);
        const row = ((colPos - 1) % colCount) + 2;
        const style = { gridColumn: col, gridRow: row, msGridColumn: col, msGridRow: row };

        const path = generateTilePath(link, this.props.selectedRole.location);
        links.push(
          <a href={path} className="launch-link wrapper" key={`Launch_Link_${i}`} style={style}>
            <div className="rec-title">{link.text}</div>
          </a>
        );
      }
    });

    if (links.length === 0) {
      return <div className="rec-title empty">{`No links for ${bucketName} at this location.`}</div>;
    }

    return links;
  }

  renderHeaderDomains() {
    let domains = [];
    if (this.props.domainsForLocationId && this.props.selectedRole && this.props.selectedRole.location) {
      const loc = this.props.selectedRole.location;
      const key = `${loc.location_id}_${loc.location_type}`;
      domains = get(this.props.domainsForLocationId, `${key}.domains`, []);
    }

    const buckets = Object.keys(launchBuckets);
    return buckets.map((name) => {
      const domCount = domains.filter((d) => domainToTileMap[d] && domainToTileMap[d].bucket === name).length;
      const colCount = Math.ceil(Math.sqrt(domCount));

      const Trigger = (props) => {
        const { innerRef, isDisabled, isExpanded, ...rest } = props;

        let arrowIcon = <Icons.IconChevronDown />;
        if (isExpanded) {
          arrowIcon = <Icons.IconChevronUp />;
        }

        return (
          <Button
            className="ribbon-bucket-button"
            appearance="subtle"
            innerRef={innerRef}
            isDisabled={isDisabled}
            {...rest}
          >
            {name}
            <div> {arrowIcon} </div>
          </Button>
        );
      };
      return (
        <div className="ribbon-bucket" key={`bucket_${name}`}>
          <DropdownBase id={`bucket_${name}`} applyButtonLabel="Close" triggerButtonElement={<Trigger />}>
            <Heading headingLevel={4} appearance="h5" className="ribbon-bucket-expand-title">
              {name}
            </Heading>
            <div className="ribbon-bucket-dropdown-content">{this.renderLinks(name, domains, colCount)}</div>
          </DropdownBase>
        </div>
      );
    });
  }

  renderHeaderSub() {
    return (
      <nav className="header-ribbon-sub">
        <div className="primary-links-wrap">{this.renderHeaderDomains()}</div>
        <div className="location-info-wrap">
          <div className="ribbon-location-select">
            <LocationPicker
              id="site-wrapper"
              selectedLocation={this.props.selectedLocation}
              launchButtonIconIsChevron
              launchButtonAppearance="subtle"
              launchButtonRoleText={`${displayStringForRoleType(
                this.props.selectedRole ? this.props.selectedRole.role_type : 'N/A'
              )}`}
              launchButtonClassName="ribbon-location-display"
              locationSelect={(location) => this.props.getDomainsForLocation(location, this.props.history)}
            />
          </div>
        </div>
      </nav>
    );
  }

  render() {
    return (
      <div
        className="header-ribbon"
        onMouseLeave={this.onRibbonLeave.bind(this)}
        onMouseEnter={this.onRibbonEnter.bind(this)}
      >
        {this.renderHeaderMain()}
        {this.renderHeaderSub()}
      </div>
    );
  }
}

HeaderRibbon.propTypes = propTypes;
